document.addEventListener('DOMContentLoaded', () => {
  // Debounce mechanism to prevent double alerts
  let lastAlertTime = 0;
  const ALERT_DEBOUNCE_MS = 1000; // Prevent duplicate alerts within 1 second

  // Function to get the checkbox (handles both regular cart and cart drawer)
  function getAgreeCheckbox() {
    // Try multiple selectors to find the checkbox
    return document.getElementById('agree') || 
           document.querySelector('input[type="checkbox"]#agree') ||
           document.querySelector('input[name="agree"]') ||
           document.querySelector('.cart-drawer input[type="checkbox"]');
  }

  // Function to check if terms are accepted
  function areTermsAccepted() {
    const checkbox = getAgreeCheckbox();
    if (!checkbox) {
      return true; // If no checkbox exists, don't block checkout
    }
    
    return checkbox.checked;
  }

  // Function to show alert with debounce protection
  function showTermsAlert() {
    const now = Date.now();
    if (now - lastAlertTime < ALERT_DEBOUNCE_MS) {
      return;
    }
    
    lastAlertTime = now;
    const message = (window.Shopify && window.Shopify.translations && window.Shopify.translations.cartAcceptTerms) 
                   || 'Please accept the terms and conditions to continue.';
    alert(message);
    
    // Focus and scroll to checkbox
    const checkbox = getAgreeCheckbox();
    if (checkbox) {
      checkbox.focus();
      checkbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Enhanced checkout button selectors
  const CHECKOUT_SELECTORS = [
    '[name="checkout"]',
    '[type="submit"][name="checkout"]',
    'button[name="checkout"]',
    '[name="goto_pp"]',
    '[name="goto_gc"]',
    'a[href*="/checkout"]',
    '.checkout-button',
    '.btn-checkout',
    '[data-checkout]'
  ].join(',');

  // Main click handler
  document.body.addEventListener('click', (e) => {
    const clickedElement = e.target.closest(CHECKOUT_SELECTORS);
    
    if (!clickedElement) {
      return; // Not a checkout button
    }
    
    if (!areTermsAccepted()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showTermsAlert();
      return false;
    }
  }, { capture: true });

  // Form submit handler with better form detection
  function addFormSubmitHandler() {
    // Find all possible cart forms
    const forms = [
      document.getElementById('cart'),
      document.querySelector('form[action*="/cart"]'),
      document.querySelector('.cart-drawer form'),
      document.querySelector('form[action*="checkout"]'),
      ...document.querySelectorAll('form')
    ].filter(form => form && (
      form.action.includes('/cart') || 
      form.action.includes('/checkout') ||
      form.querySelector('[name="checkout"]')
    ));

    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        // Check if this form has a checkout button
        const hasCheckoutButton = form.querySelector(CHECKOUT_SELECTORS);
        if (!hasCheckoutButton) {
          return; // Not a checkout form
        }

        if (!areTermsAccepted()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showTermsAlert();
          return false;
        }
      }, { capture: true });
    });
  }

  // Initial form handler setup
  addFormSubmitHandler();

  // Re-run form handler setup when DOM changes (for AJAX cart updates)
  const observer = new MutationObserver((mutations) => {
    let shouldRecheck = false;
    
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.matches && (
                node.matches('form') || 
                node.matches('.cart-drawer') ||
                node.querySelector && node.querySelector('form')
            )) {
              shouldRecheck = true;
            }
          }
        });
      }
    });

    if (shouldRecheck) {
      setTimeout(addFormSubmitHandler, 100);
    }
  });

  // Observe the entire document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Additional: Track checkbox state changes
  const initialCheckbox = getAgreeCheckbox();
  if (initialCheckbox) {
    initialCheckbox.addEventListener('change', (e) => {
      // Checkbox state changed
    });
  }

  // Search functionality - Hide recommended sections when searching
  function initializeSearchBehavior() {
    const searchInput = document.querySelector('.header-search__input');
    const searchContent = document.querySelector('.search-content');
    const recommendedCategories = document.querySelector('.search__recommended-categories');
    const recommendedProducts = document.querySelector('.search__recommended-products');
    
    if (!searchInput || !searchContent) {
      return; // Search elements not found
    }

    // Function to show/hide recommended sections
    function toggleRecommendedSections(show) {
      if (recommendedCategories) {
        recommendedCategories.style.display = show ? 'block' : 'none';
      }
      if (recommendedProducts) {
        recommendedProducts.style.display = show ? 'block' : 'none';
      }
    }

    // Hide recommended sections when user starts typing
    searchInput.addEventListener('input', (e) => {
      const hasQuery = e.target.value.trim().length > 0;
      toggleRecommendedSections(!hasQuery);
    });

    // Show recommended sections when search is cleared
    searchInput.addEventListener('focus', (e) => {
      const hasQuery = e.target.value.trim().length > 0;
      toggleRecommendedSections(!hasQuery);
    });

    // Reset when search modal is closed and reopened
    const searchModal = document.querySelector('header-search');
    if (searchModal) {
      // Use MutationObserver to detect when search modal is opened/closed
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            // Search modal state changed
            if (searchModal.hasAttribute('open')) {
              // Modal opened - show recommended sections if no query
              const hasQuery = searchInput.value.trim().length > 0;
              toggleRecommendedSections(!hasQuery);
            }
          }
        });
      });
      
      observer.observe(searchModal, { attributes: true });
    }
  }

  // Initialize search behavior
  initializeSearchBehavior();

  // Geo / region popup - first-visit region chooser (mirrors header language-switcher)
  const GEO_POPUP_SEEN_KEY = 'ccl:geo-popup-seen';
  const GEO_CACHE_KEY = 'ccl:geo-data';
  const GEO_API_URL = 'https://ipapi.co/json/';

  // Test IPs that always see the popup, even from Denmark (for QA)
  const GEO_TEST_IPS = ['87.54.46.242'];

  // Resolve visitor geo data, cached per session to limit ipapi calls
  async function resolveGeo() {
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }

    const response = await fetch(GEO_API_URL);
    if (!response.ok) {
      throw new Error('ipapi lookup failed with status ' + response.status);
    }

    const data = await response.json();
    const geo = { ip: data.ip || '', countryCode: data.country_code || '' };
    sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(geo));
    return geo;
  }

  function openGeoPopup(popup, isDesignMode) {
    function persist() {
      if (!isDesignMode) {
        localStorage.setItem(GEO_POPUP_SEEN_KEY, 'true');
      }
    }

    function dismiss() {
      persist();
      popup.classList.remove('geo-popup--open');
      document.body.classList.remove('geo-popup-open');
      setTimeout(() => popup.remove(), 300);
    }

    popup.hidden = false;
    requestAnimationFrame(() => {
      popup.classList.add('geo-popup--open');
      document.body.classList.add('geo-popup-open');
    });

    // Region choice: persist, then let navigation proceed
    popup.querySelectorAll('[data-geo-popup-choice]').forEach((el) => {
      el.addEventListener('click', persist);
    });

    // Dismiss controls (close button + backdrop)
    popup.querySelectorAll('[data-geo-popup-dismiss]').forEach((el) => {
      el.addEventListener('click', dismiss);
    });

    // Esc to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('geo-popup--open')) {
        dismiss();
      }
    });
  }

  async function initGeoPopup() {
    const popup = document.querySelector('.geo-popup');
    if (!popup) {
      return; // Section disabled or not on page
    }

    const isDesignMode = popup.dataset.designMode === 'true';

    // Design mode always previews, skipping storage + geo checks
    if (isDesignMode) {
      openGeoPopup(popup, true);
      return;
    }

    // First-visit gate: skip if already seen
    if (localStorage.getItem(GEO_POPUP_SEEN_KEY)) {
      popup.remove();
      return;
    }

    // Only show to visitors outside Denmark. On lookup failure, fail closed.
    let geo;
    try {
      geo = await resolveGeo();
    } catch (error) {
      console.error('Geo popup: country lookup failed, not showing popup.', error);
      popup.remove();
      return;
    }

    // Test IPs bypass the Denmark gate so the popup can be QA'd from DK
    if (geo.countryCode === 'DK' && !GEO_TEST_IPS.includes(geo.ip)) {
      popup.remove();
      return;
    }

    openGeoPopup(popup, false);
  }

  initGeoPopup();
});